import { firestore } from '../firebaseConfig'; // Firestore setup
import { arrayUnion, arrayRemove, doc, Timestamp, getDoc, runTransaction, increment } from 'firebase/firestore'; // Firestore functions
import { fetchSentenceWords } from './course';
import { getAddItemStatsUpdates, getRemoveItemStatsUpdates } from './metadataStats';

/**
 * Adds a sentence to the user's learning list atomically if they meet criteria.
 * Creates an entry in the user_sentences subcollection and adds the ID
 * to the user_sentences_ids array in the main user document.
 * Uses a Batched Write for atomic updates.
 * @param {string} userId - The ID of the user.
 * @param {string} sentenceId - The ID of the sentence to add.
 * @returns {Promise<{status: string, sentenceId: string}>} - Success status and sentence ID.
 * @throws {Error} - Throws specific errors for known issues (premium, not found, permission) or re-throws others.
 */
export const addSentence = async (userId, sentenceId, isSubscribed) => {
  // Document References
  const sentenceDocRef = doc(firestore, 'users', userId, 'user_sentences', sentenceId);
  const sentenceDataRef = doc(firestore, 'sentences', sentenceId);
  const userSentencesIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_sentences_ids');

  try {
    // 1. Fetch the main sentence data
    const sentenceSnapshot = await getDoc(sentenceDataRef);
    if (!sentenceSnapshot.exists()) {
      console.error(`Sentence ${sentenceId} not found in the main 'sentences' collection.`);
      // Throw specific error
      const notFoundError = new Error(`Sentence ${sentenceId} not found.`);
      notFoundError.code = 'sentence-not-found';
      throw notFoundError;
    }
    const sentenceData = sentenceSnapshot.data();
    const sentenceLevel = sentenceData.level;
    const sentenceWkLevel = sentenceData.kanji_complexity;

    // 2. Check user subscription status

    // 3. Enforce subscription rules
    // Assuming level 0 is free, levels 1-4 require premium
    if (!isSubscribed && sentenceLevel > 0 && sentenceLevel < 5) {
      const error = new Error(`Premium required for Level ${sentenceLevel} sentence.`);
      error.code = 'premium-required'; // Custom code
      error.level = sentenceLevel;
      throw error;
    }

    // 4. Transaction with existence check to prevent double-add and double metadata increment
    await runTransaction(firestore, async (transaction) => {
      const existingDoc = await transaction.get(sentenceDocRef);
      if (existingDoc.exists()) return; // Already added, skip

      const curr_full_timestamp = Timestamp.now();
      const newUserSentenceData = {
        sentence_ref: sentenceDataRef,
        srs_stage: 0,
        correct: 0,
        wrong: 0,
        learned_at: curr_full_timestamp,
        next_review: curr_full_timestamp,
        skipped: false,
        level: sentenceLevel,
      };

      transaction.set(sentenceDocRef, newUserSentenceData);
      transaction.set(userSentencesIdsRef, { data: arrayUnion(sentenceId) }, { merge: true });
      getAddItemStatsUpdates(transaction, userId, 'sentences', sentenceLevel, 0, false, sentenceWkLevel);
    });

    return { status: 'fulfilled', sentenceId: sentenceId };
  } catch (error) {
    // Handle specific known errors
    if (error.code === 'premium-required' || error.code === 'sentence-not-found') {
      throw error; // Re-throw specific errors we created
    }
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied. Please ensure you are logged in and have verified your email if required.');
      permissionError.code = 'permission-denied-addsentence';
      throw permissionError;
    }

    // Log and re-throw unexpected errors
    console.error(`Error adding sentence ${sentenceId} for user ${userId}:`, error);
    throw error; // Re-throw for the caller
  }
};

/**
 * Deletes a sentence from the user's learning list atomically.
 * Removes the sentence document from the user_sentences subcollection and
 * removes the sentence ID from the user_sentences_ids array in the main user document.
 * Uses a Batched Write for atomic updates.
 * @param {string} userId - The ID of the user.
 * @param {string} sentenceId - The ID of the sentence to delete.
 * @returns {Promise<{status: string, sentenceId: string}>} - Success status and sentence ID.
 * @throws {Error} - Throws specific errors for known issues (e.g., permission) or re-throws others.
 */
export const deleteSentence = async (userId, sentenceId) => {
  // Document References
  const sentenceDocRef = doc(firestore, 'users', userId, 'user_sentences', sentenceId); // Ref in subcollection
  const userSentencesIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_sentences_ids');

  try {
    // Transaction moves the read inside to prevent concurrent double-decrement
    await runTransaction(firestore, async (transaction) => {
      const sentenceDoc = await transaction.get(sentenceDocRef);
      if (!sentenceDoc.exists()) return; // Already deleted

      const sentenceData = sentenceDoc.data();
      const level = sentenceData.level;
      const srsStage = sentenceData.srs_stage || 0;
      const skipped = sentenceData.skipped || false;
      const correctCount = sentenceData.correct || 0;
      const wrongCount = sentenceData.wrong || 0;

      // Fetch the global sentence to get kanji_complexity for the wk-bucket decrement.
      // Reads must precede writes in Firestore transactions.
      const globalSentenceRef = sentenceData.sentence_ref || doc(firestore, 'sentences', sentenceId);
      const globalSentenceSnap = await transaction.get(globalSentenceRef);
      const wkLevel = globalSentenceSnap.data().kanji_complexity;

      transaction.delete(sentenceDocRef);
      transaction.set(userSentencesIdsRef, { data: arrayRemove(sentenceId) }, { merge: true });
      getRemoveItemStatsUpdates(transaction, userId, 'sentences', level, srsStage, skipped, correctCount, wrongCount, wkLevel);
    });

    return { status: 'fulfilled', sentenceId: sentenceId };
  } catch (error) {
    // Handle specific errors (e.g., permissions)
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied while trying to delete the sentence.');
      permissionError.code = 'permission-denied-deletesentence';
      throw permissionError;
    }
    // Note: Deleting a non-existent doc doesn't error, batch ensures atomicity regardless.

    // Log and re-throw unexpected errors
    console.error(`Error deleting sentence ${sentenceId} for user ${userId}:`, error);
    throw error; // Re-throw for the calling component to handle
  }
};

/**
 * Marks a sentence as skipped for a user, adding it first if necessary.
 * Uses a Transaction to ensure the entire operation is atomic.
 * @param {string} userId - The ID of the user.
 * @param {string} sentenceId - The ID of the sentence to skip.
 * @returns {Promise<{status: string, sentenceId: string, skipped: boolean}>} - Success status.
 * @throws {Error} - Throws specific errors for known issues (premium, not found, permission) or re-throws others.
 */
export const skipSentence = async (userId, sentenceId, isSubscribed) => {
  // Document References
  const userSentenceDocRef = doc(firestore, 'users', userId, 'user_sentences', sentenceId);
  const mainSentenceDataRef = doc(firestore, 'sentences', sentenceId);
  const userSentencesIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_sentences_ids');

  try {
    // --- Pre-Transaction Checks ---
    // 1. Fetch main sentence data for level check
    const mainSentenceSnapshot = await getDoc(mainSentenceDataRef);
    if (!mainSentenceSnapshot.exists()) {
      console.error(`Sentence ${sentenceId} not found in the main 'sentences' collection.`);
      const notFoundError = new Error(`Sentence ${sentenceId} not found.`);
      notFoundError.code = 'sentence-not-found';
      throw notFoundError;
    }
    const mainSentenceData = mainSentenceSnapshot.data();
    const sentenceLevel = mainSentenceData.level;
    const sentenceWkLevel = mainSentenceData.kanji_complexity;

    // 2. Check subscription status

    // 3. Enforce subscription rules (before transaction)
    // Assuming level 0 is free, levels 1-4 require premium
    if (!isSubscribed && sentenceLevel > 0 && sentenceLevel < 5) {
      const error = new Error(`Premium required to interact with Level ${sentenceLevel} sentence.`);
      error.code = 'premium-required';
      error.level = sentenceLevel;
      throw error;
    }
    // --- End Pre-Transaction Checks ---

    // --- Start Transaction ---
    const alreadySkipped = await runTransaction(firestore, async (transaction) => {
      // 4. Read the user's specific sentence document *within the transaction*
      const userSentenceDoc = await transaction.get(userSentenceDocRef);
      const curr_full_timestamp = Timestamp.now();

      if (userSentenceDoc.exists()) {
        // --- Case 1: Sentence already exists in user's list ---
        const existingData = userSentenceDoc.data();
        if (existingData.skipped) return true; // Already skipped, signal caller
        const currentSrsStage = existingData.srs_stage || 0;
        
        // Update metadata stats: move from current SRS stage to skipped
        const srsUpdate = {
          sentences: {
            [currentSrsStage]: increment(-1),
            skipped: increment(1)
          }
        };
        const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
        transaction.set(srsRef, srsUpdate, { merge: true });
        
        // Update the existing document to mark as skipped.
        transaction.update(userSentenceDocRef, { skipped: true });
      } else {
        // --- Case 2: Sentence does NOT exist in user's list ---
        // Add the sentence, marked as skipped, and update the user's sentence list array.

        // Define the data for the new document, setting skipped: true
        // (This mirrors the data structure from addSentence)
        const newSentenceData = {
          sentence_ref: mainSentenceDataRef,
          srs_stage: 0,
          correct: 0,
          wrong: 0,
          learned_at: curr_full_timestamp,
          next_review: curr_full_timestamp, // Or adjust if skipping affects review time
          skipped: true, // Set skipped directly
          level: sentenceLevel,
        };

        // Update metadata stats for new skipped sentence
        const levelUpdate = {
          sentences_by_wk: {
            [sentenceLevel]: {
              [sentenceWkLevel]: increment(1)
            }
          }
        };
        const levelRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_level');
        transaction.set(levelRef, levelUpdate, { merge: true });
        
        const srsUpdate = {
          sentences: {
            skipped: increment(1)
          }
        };
        const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
        transaction.set(srsRef, srsUpdate, { merge: true });
        
        // Queue the writes
        transaction.set(userSentenceDocRef, newSentenceData); // Set the new skipped sentence data
        transaction.set(
          userSentencesIdsRef,
          {
            data: arrayUnion(sentenceId),
          },
          { merge: true }
        );
      }
    }); // --- End Transaction ---

    if (alreadySkipped) return { status: 'already_skipped', sentenceId };
    return { status: 'fulfilled', sentenceId: sentenceId, skipped: true };
  } catch (error) {
    // Handle specific known errors (some caught before transaction)
    if (error.code === 'premium-required' || error.code === 'sentence-not-found') {
      throw error; // Re-throw specific errors
    }
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied during skip operation. Please ensure you are logged in and authorized.');
      permissionError.code = 'permission-denied-skipsentence';
      throw permissionError;
    }

    // Log and re-throw unexpected errors from transaction etc.
    console.error(`Error skipping sentence ${sentenceId} for user ${userId}:`, error);
    throw error; // Re-throw for the caller
  }
};

/**
 * Unskips a sentence for a user by setting its 'skipped' field to false.
 * Uses a transaction to update both the sentence and metadata stats atomically.
 * @param {string} userId - The ID of the user.
 * @param {string} sentenceId - The ID of the sentence to unskip.
 * @returns {Promise<{status: string, sentenceId: string}>} - Success status and sentence ID.
 * @throws {Error} - Throws specific errors for known issues (e.g., permission, not found) or re-throws others.
 */
export const unskipSentence = async (userId, sentenceId) => {
  // Document Reference to the specific sentence in the user's subcollection
  const sentenceDocRef = doc(firestore, 'users', userId, 'user_sentences', sentenceId);

  try {
    // Use transaction to update both the sentence and metadata stats
    await runTransaction(firestore, async (transaction) => {
      const sentenceDoc = await transaction.get(sentenceDocRef);
      
      if (!sentenceDoc.exists()) {
        throw new Error(`Sentence ${sentenceId} not found in user's list.`);
      }
      
      const sentenceData = sentenceDoc.data();
      if (!sentenceData.skipped) return; // Already unskipped, no-op
      const srsStage = sentenceData.srs_stage || 0;

      // Update metadata stats: move from skipped back to original SRS stage
      const srsUpdate = {
        sentences: {
          skipped: increment(-1),
          [srsStage]: increment(1)
        }
      };
      const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
      transaction.set(srsRef, srsUpdate, { merge: true });
      
      // Update the document, setting skipped to false
      transaction.update(sentenceDocRef, {
        skipped: false,
      });
    });

    return { status: 'fulfilled', sentenceId: sentenceId };
  } catch (error) {
    // Handle specific Firestore errors
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied while trying to unskip the sentence.');
      permissionError.code = 'permission-denied-unskipsentence';
      throw permissionError;
    }
    if (error.code === 'not-found') {
      const notFoundError = new Error(`Cannot unskip sentence '${sentenceId}' because it was not found in the user's list.`);
      notFoundError.code = 'not-found-unskipsentence';
      throw notFoundError;
    }

    // Log and re-throw unexpected errors
    console.error(`Error unskipping sentence ${sentenceId} for user ${userId}:`, error);
    throw error; // Re-throw for the calling component to handle
  }
};

/**
 * Fetches complete details for a single sentence, including its words and user-specific progress.
 * @param {string} sentenceId - The ID of the sentence to fetch.
 * @param {string} [userId] - The optional ID of the logged-in user.
 * @returns {Promise<object>} A complete sentence object with all details.
 */
export const getSentenceDetails = async (sentenceId, userId) => {
  if (!sentenceId) throw new Error('Sentence ID is required.');

  // 1. Get the base sentence data
  const sentenceDocRef = doc(firestore, 'sentences', sentenceId);
  const sentenceDocSnap = await getDoc(sentenceDocRef);

  if (!sentenceDocSnap.exists()) {
    throw new Error(`Sentence with ID ${sentenceId} not found.`);
  }

  let sentenceData = { id: sentenceId, ...sentenceDocSnap.data() };

  // 2. Fetch the full data for each word in the sentence
  if (sentenceData.words && sentenceData.words.length > 0) {
    sentenceData.words = await fetchSentenceWords(sentenceData.words);
  }

  // 3. If a user is logged in, get their specific progress
  if (userId) {
    const userSentenceDocRef = doc(firestore, 'users', userId, 'user_sentences', sentenceId);
    const userSentenceDocSnap = await getDoc(userSentenceDocRef);

    if (userSentenceDocSnap.exists()) {
      const userProgress = userSentenceDocSnap.data();
      // Merge base data, word data, and user progress
      return {
        ...sentenceData,
        ...userProgress,
        isLearned: true,
      };
    }
  }

  // If no user or no user progress, return the base data with defaults
  return {
    ...sentenceData,
    isLearned: false,
    srsStage: 0,
    skipped: false,
  };
};
